import { Authenticator } from '@aws-amplify/ui-react';
import { Amplify, Auth } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { envConfig } from '../../configuration/aws-exports';
import { fetchFeatureFlags } from 'src/config/featureFlags';
import { FullPageSpinner } from 'src/common/FullPageSpinner';

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  children: JSX.Element;
}

/**
 * AuthProvider component handles authentication and configuration for the application.
 * It uses AWS Amplify for authentication and manages the authentication state.
 *
 * @param {AuthProviderProps} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [isConfiguring, setIsConfiguring] = useState(true);

  useEffect(() => {
    /**
     * Configures Amplify and checks the current authentication state.
     * If not authenticated, it initiates federated sign-in.
     */
    const configure = async () => {
      try {
        Amplify.configure(envConfig);
        await Auth.currentAuthenticatedUser();
        // Warm the feature-flag cache so AI_ASSISTANT is available synchronously later.
        await fetchFeatureFlags();
        // Orion is the standard theme — set unconditionally before first paint.
        document.body.classList.add('orion-ui');
        setIsConfiguring(false);
      } catch (error) {
        console.error(error);
        // Store the current URL before redirecting
        //sessionStorage.setItem('redirectUrl', window.location.pathname + window.location.search);
        Auth.federatedSignIn({
          customProvider: 'AmazonFederate',
          // Pass the redirect URL as a custom state if the IdP supports it
        });
      }
    };
    configure();
  }, []);

  useEffect(() => {
    /**
     * Handles redirection after successful authentication.
     */
    if (!isConfiguring) {
      // Check for stored redirect URL after successful authentication
      const redirectUrl = sessionStorage.getItem('redirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        //Disabling to handle fortify issue.
        //TODO: Handle redirection after midway auth
        //window.location.href = redirectUrl;
      }
    }
  }, [isConfiguring]);

  if (isConfiguring) {
    return <FullPageSpinner />;
  }

  return (
    <React.Suspense fallback={<FullPageSpinner />}>
      <Authenticator.Provider>{children}</Authenticator.Provider>
    </React.Suspense>
  );
};
